import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';
import { Task, TaskColumn, OfficeId } from '../../lib/types';
import { KANBAN_COLUMNS, OFFICES, getOffice } from '../../lib/config';
import { apiListTasks, apiCreateTask, apiUpdateTask, apiDeleteTask } from '../../lib/api';
import { TaskAssignmentButton } from '../tasks/TaskAssignmentButton';
import OfficeContextBanner from '../OfficeContextBanner';

interface KanbanBoardProps {
  /** If provided, filters tasks to this office only */
  officeId?: OfficeId;
}

/**
 * KanbanBoard — four-column task board.
 * Tasks are color-coded by office. Cards can be moved between columns.
 * Filtered to a specific office when officeId prop is provided.
 */
export default function KanbanBoard({ officeId }: KanbanBoardProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [mobileCol, setMobileCol] = useState<TaskColumn>('todo');

  const fetchTasks = useCallback(() => {
    setLoading(true);
    apiListTasks(officeId)
      .then(setTasks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [officeId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const moveTask = async (taskId: string, column: TaskColumn) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, column } : t)));
    await apiUpdateTask(taskId, { column }).catch((err: any) => {
      if (err.response?.status === 501) {
        // Column move not yet available — silently revert
      }
      fetchTasks();
    });
  };

  const deleteTask = async (taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    await apiDeleteTask(taskId).catch((err: any) => {
      if (err.response?.status === 501) {
        // Delete not yet available — silently revert
      }
      fetchTasks();
    });
  };

  const addTask = async (task: Partial<Task>) => {
    const created = await apiCreateTask({ ...task, column: 'todo' });
    setTasks((prev) => [created, ...prev]);
    setShowAddModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="kanban-board">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-2">
          <OfficeContextBanner officeId={officeId} />
          <span className="text-xs text-gray-400">{tasks.length} tasks</span>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 transition-colors active:scale-95"
          data-testid="kanban-add-task-button"
        >
          <Plus size={13} />
          Add Task
        </button>
      </div>

      {/* Mobile column tabs */}
      <div className="md:hidden flex border-b border-gray-100 shrink-0 bg-gray-50">
        {KANBAN_COLUMNS.map((col) => {
          const count = tasks.filter((t) => t.column === col.id).length;
          const isActive = mobileCol === col.id;
          return (
            <button
              key={col.id}
              onClick={() => setMobileCol(col.id as TaskColumn)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${isActive ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-400'}`}
              data-testid={`kanban-mobile-tab-${col.id}`}
            >
              {col.label}
              <span className={`ml-1 text-[10px] rounded-full px-1.5 py-0.5 ${isActive ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Mobile: single column view */}
      <div className="md:hidden flex-1 overflow-y-auto p-3 space-y-2">
        {tasks.filter((t) => t.column === mobileCol).map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            currentColumn={mobileCol}
            onMove={moveTask}
            onDelete={deleteTask}
          />
        ))}
        {tasks.filter((t) => t.column === mobileCol).length === 0 && (
          <div className="h-24 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center">
            <span className="text-xs text-gray-400">No tasks here</span>
          </div>
        )}
      </div>

      {/* Desktop: all columns */}
      <div className="hidden md:flex gap-4 overflow-x-auto p-5 flex-1 min-h-0">
        {KANBAN_COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.column === col.id);
          return (
            <div
              key={col.id}
              className="w-72 shrink-0 flex flex-col"
              data-testid={`kanban-column-${col.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{col.label}</span>
                <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-medium">{colTasks.length}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1 overflow-y-auto">
                {colTasks.map((task) => (
                  <TaskCard key={task.id} task={task} currentColumn={col.id} onMove={moveTask} onDelete={deleteTask} />
                ))}
                {colTasks.length === 0 && (
                  <div className="h-16 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center">
                    <span className="text-xs text-gray-400">No tasks</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAddModal && (
        <AddTaskModal
          officeId={officeId}
          onAdd={addTask}
          onCancel={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

// ── Task Card ─────────────────────────────────────────────────────────────────

function TaskCard({
  task, currentColumn, onMove, onDelete,
}: {
  task: Task;
  currentColumn: string;
  onMove: (id: string, col: TaskColumn) => void;
  onDelete: (id: string) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const office = getOffice(task.office_id);

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-3.5 shadow-sm hover:border-gray-300 transition-all group relative"
      data-testid={`kanban-card-${task.id}`}
    >
      {/* Office color indicator */}
      {office && (
        <div
          className="absolute left-0 top-3 bottom-3 w-0.5 rounded-r"
          style={{ backgroundColor: office.color }}
        />
      )}

      <div className="pl-2">
        <p className="text-sm font-medium text-gray-900 leading-snug mb-1.5">{task.title}</p>
        {task.description && (
          <p className="text-xs text-gray-500 mb-2 leading-relaxed">{task.description}</p>
        )}

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1.5">
            {office && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ backgroundColor: `${office.color}20`, color: office.color }}
              >
                {office.name}
              </span>
            )}
            {task.agent_name && (
              <span className="text-[10px] text-gray-400 font-medium">{task.agent_name}</span>
            )}
          </div>

          <TaskAssignmentButton taskId={task.id} currentAssignee={task.agent_name} />

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 text-gray-400 hover:text-gray-700 text-xs px-1 py-0.5 rounded hover:bg-gray-100 transition-all"
              data-testid={`kanban-card-menu-${task.id}`}
            >
              Move <ChevronDown size={10} />
            </button>

            {showMenu && (
              <div className="absolute right-0 bottom-7 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-32">
                {KANBAN_COLUMNS.filter((c) => c.id !== currentColumn).map((col) => (
                  <button
                    key={col.id}
                    onClick={() => { onMove(task.id, col.id as TaskColumn); setShowMenu(false); }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    data-testid={`move-to-${col.id}`}
                  >
                    → {col.label}
                  </button>
                ))}
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => { onDelete(task.id); setShowMenu(false); }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50"
                    data-testid={`delete-task-${task.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add Task Modal ─────────────────────────────────────────────────────────────

function AddTaskModal({
  officeId, onAdd, onCancel,
}: {
  officeId?: string;
  onAdd: (task: Partial<Task>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [selectedOffice, setSelectedOffice] = useState(officeId || 'home');
  const [agentName, setAgentName] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" data-testid="add-task-modal">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Add Task</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-testid="close-add-task-modal">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="add-task-title-input"
            autoFocus
          />
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
            data-testid="add-task-desc-input"
          />
          {!officeId && (
            <select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
              data-testid="add-task-office-select"
            >
              {OFFICES.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <input
            type="text"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="Assigned agent (optional)"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="add-task-agent-input"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="cancel-add-task"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!title.trim()) return;
              onAdd({ title: title.trim(), description: desc.trim() || undefined, office_id: selectedOffice, agent_name: agentName.trim() || undefined });
            }}
            disabled={!title.trim()}
            className="flex-1 rounded-lg bg-gray-900 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-40 transition-colors"
            data-testid="confirm-add-task"
          >
            Add Task
          </button>
        </div>
      </div>
    </div>
  );
}
